import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { indentUnit } from '@codemirror/language'
import { loadPyodide } from 'pyodide'
import {
  ChevronLeft, ChevronDown, HelpCircle, FileText, Cat, History,
  ListChecks, Info, Settings, ChevronsLeft, X, RotateCw, Play, MoreVertical,
  Send, Loader2
} from 'lucide-react'
import '../css/chi.scss'
import { A1_STARTER } from '../study/content/canonicalContent.generated'

// /chi is a visual/component demo, NOT the study flow. Its editor seeds with the
// canonical A1 starter (single source of truth: src/study/content/canonical/).
// The prior hardcoded STARTER_CODE drifted from canonical (wrong timer signature)
// and was deleted per the study brief.
const STARTER_CODE = A1_STARTER

// Cosmo assistant endpoint (backend proxies to OpenAI, key stays server-side).
// NOTE: the study tutor's Socratic system prompt is server-side and session-keyed
// (never a client-side system prompt) — wired in the /study flow, not here.
const GPT_HOST = process.env.REACT_APP_HOST + '/api/v1/gpt/openAI'

// Load Pyodide once and reuse it across runs
let pyodidePromise = null
function getPyodide () {
  if (!pyodidePromise) {
    pyodidePromise = loadPyodide({
      indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.23.4/full/'
    })
  }
  return pyodidePromise
}

// Left icon rail items — DESC is active in the reference
const RAIL = [
  { key: 'desc', label: 'DESC', Icon: FileText },
  { key: 'cosmo', label: 'COSMO', Icon: Cat },
  { key: 'history', label: 'HISTORY', Icon: History },
  { key: 'rules', label: 'RULES', Icon: ListChecks },
  { key: 'info', label: 'INFO', Icon: Info },
  { key: 'settings', label: 'SETTINGS', Icon: Settings }
]

let msgId = 0

export default function ChiPage () {
  const [active, setActive] = useState('desc')
  const [showTip, setShowTip] = useState(true)
  const [messages, setMessages] = useState([
    { id: ++msgId, from: 'cosmo', text: "Hi, I'm Cosmo — your AI assistant. Ask me anything about this task and I'll help you work through it." }
  ])
  const [input, setInput] = useState('')
  const [isReplying, setIsReplying] = useState(false)
  const scrollRef = useRef(null)

  // ── Code editor + runner ──────────────────────────────────────
  const [code, setCode] = useState(STARTER_CODE)
  const [consoleTab, setConsoleTab] = useState('terminal') // 'output' | 'terminal'
  const [output, setOutput] = useState('')
  const [isRunning, setIsRunning] = useState(false)

  const runCode = async () => {
    if (isRunning) return
    setIsRunning(true)
    setConsoleTab('output')
    setOutput('Loading Python runtime…')
    try {
      const pyodide = await getPyodide()
      let buffer = ''
      const sink = { isatty: false, batched: (s) => { buffer += s + '\n' } }
      pyodide.setStdout(sink)
      pyodide.setStderr(sink)
      setOutput('Running your code…')
      try {
        await pyodide.runPythonAsync(code)
        setOutput(buffer.trimEnd() || 'Code executed successfully (no output).')
      } catch (err) {
        setOutput((buffer ? buffer + '\n' : '') + String(err.message || err))
      }
    } catch (err) {
      setOutput('Failed to start Python runtime: ' + (err.message || err))
    } finally {
      setIsRunning(false)
    }
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, active])

  const sendMessage = async () => {
    const text = input.trim()
    if (!text || isReplying) return
    const userMsg = { id: ++msgId, from: 'user', text }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setIsReplying(true)
    try {
      // Prior turns as OpenAI-format history (greeting + past exchanges)
      const history = messages.map(m => ({
        role: m.from === 'cosmo' ? 'assistant' : 'user',
        content: m.text
      }))
      // Attach the student's current editor code to the new question
      const enrichedUser = `${text}\n\n[My current code]\n\`\`\`python\n${code}\n\`\`\``
      // No client-side system prompt (study rule: the tutor prompt is server-side).
      const apiMessages = [
        ...history,
        { role: 'user', content: enrichedUser }
      ]
      const res = await axios.post(GPT_HOST, { messages: apiMessages })
      const reply = (res.data && res.data.content) || "Sorry, I couldn't generate a response."
      setMessages(prev => [...prev, { id: ++msgId, from: 'cosmo', text: reply }])
    } catch (err) {
      const detail = (err.response && err.response.data) || err.message || 'unknown error'
      setMessages(prev => [...prev, {
        id: ++msgId,
        from: 'cosmo',
        text: 'I could not reach the assistant service (' + detail + '). Make sure the backend is running.'
      }])
    } finally {
      setIsReplying(false)
    }
  }

  const onInputKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="chi-root">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="chi-topbar">
        <div className="chi-topbar__back">
          <ChevronLeft size={16} />
          <span>BACK TO QUESTIONS</span>
        </div>
        <div className="chi-topbar__center">
          <span>Question 1 of 2</span>
          <ChevronDown size={15} />
        </div>
        <div className="chi-topbar__right">
          <span>0:34:11</span>
          <HelpCircle size={18} />
        </div>
      </header>

      {/* ── Body ────────────────────────────────────────────────── */}
      <div className="chi-body">
        {/* Left description side */}
        <section className="chi-desc">
          <nav className="chi-rail">
            {RAIL.map(({ key, label, Icon }) => (
              <div
                key={key}
                className={`chi-rail__item${active === key ? ' is-active' : ''}`}
                onClick={() => setActive(key)}
              >
                <Icon size={20} strokeWidth={1.8} />
                <span>{label}</span>
              </div>
            ))}
          </nav>

          {showTip && active !== 'cosmo' && (
            <div className="chi-cosmo-tip">
              <span>Meet Cosmo, your AI assistant!</span>
              <X size={15} onClick={() => setShowTip(false)} />
            </div>
          )}

          {active === 'cosmo' ? (
          <div className="chi-cosmo">
            <div className="chi-cosmo__head">
              <div className="chi-cosmo__avatar"><Cat size={20} strokeWidth={1.8} /></div>
              <div>
                <div className="chi-cosmo__name">Cosmo</div>
                <div className="chi-cosmo__role">AI assistant</div>
              </div>
            </div>
            <div className="chi-cosmo__log" ref={scrollRef}>
              {messages.map(m => (
                <div key={m.id} className={`chi-msg chi-msg--${m.from}`}>
                  {m.from === 'cosmo' && (
                    <div className="chi-msg__avatar"><Cat size={15} strokeWidth={1.9} /></div>
                  )}
                  <div className="chi-msg__bubble">{m.text}</div>
                </div>
              ))}
              {isReplying && (
                <div className="chi-msg chi-msg--cosmo">
                  <div className="chi-msg__avatar"><Cat size={15} strokeWidth={1.9} /></div>
                  <div className="chi-msg__bubble chi-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
            </div>
            <div className="chi-cosmo__composer">
              <input
                className="chi-cosmo__input"
                placeholder="Ask Cosmo…"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onInputKey}
                disabled={isReplying}
              />
              <button className="chi-cosmo__send" onClick={sendMessage} disabled={!input.trim() || isReplying}>
                <Send size={16} />
              </button>
            </div>
          </div>
          ) : (
          <div className="chi-desc__content">
            <div className="chi-desc__head">
              <h1 className="chi-desc__title">Task 1: Concurrent Execution</h1>
              <span className="chi-desc__collapse"><ChevronsLeft size={20} /></span>
            </div>
            <hr className="chi-desc__rule" />

            <p>
              <span className="chi-strong">Setup</span>: To begin make sure you run:{' '}
              <code className="chi-code-inline">pip install trio</code> in the terminal.
            </p>

            <h2 className="chi-desc__sub">Introduction to Trio</h2>

            <p>
              Trio is a Python library designed for asynchronous programming. It provides a
              structured and easy-to-use approach to handling concurrency. Trio is particularly
              known for its focus on simplicity and correctness, making it a great choice for
              developers who want to write reliable asynchronous code without getting bogged down
              by the complexities often associated with concurrency.
            </p>

            <p>
              One of the core features of Trio is its use of "nurseries", which allow you to manage
              multiple <span className="chi-strong">concurrent</span> asynchronous tasks. For
              example, consider the following usage of a nursery in Trio:
            </p>

            <pre className="chi-code-block">
              <span className="lk">async def</span> <span className="lfn">parent</span>():{'\n'}
              {'    '}<span className="lbi">print</span>(<span className="lst">"parent: started!"</span>){'\n'}
              {'    '}<span className="lcm"># async context manager must be started with "async with"</span>{'\n'}
              {'    '}<span className="lk">async with</span> trio.<span className="lfn">open_nursery</span>() <span className="lk">as</span> nursery:{'\n'}
              {'        '}<span className="lbi">print</span>(<span className="lst">"parent: spawning child1..."</span>){'\n'}
              {'        '}nursery.<span className="lfn">start_soon</span>(child1) <span className="lcm"># schedules child1 to run concurrently</span>{'\n'}
              {'\n'}
              {'        '}<span className="lbi">print</span>(<span className="lst">"parent: spawning child2..."</span>) <span className="lcm"># this prints immediately</span>{'\n'}
              {'        '}nursery.<span className="lfn">start_soon</span>(child2) <span className="lcm"># schedules child2 to run concurrently</span>{'\n'}
              {'\n'}
              {'        '}<span className="lbi">print</span>(<span className="lst">"parent: waiting ..."</span>) <span className="lcm"># this also prints immediately</span>{'\n'}
              {'        '}<span className="lcm"># The nursery waits here until ALL child tasks complete</span>{'\n'}
              {'\n'}
              {'    '}<span className="lbi">print</span>(<span className="lst">"parent: all done!"</span>) <span className="lcm"># prints only after both children finish</span>{'\n'}
              {'\n'}
              trio.<span className="lfn">run</span>(parent) <span className="lcm">#runs a Trio async function</span>
            </pre>
          </div>
          )}
        </section>

        {/* Divider */}
        <div className="chi-divider" />

        {/* Right editor side */}
        <section className="chi-editor-side">
          <div className="chi-ed-tabs">
            <div className="chi-ed-tab">main.py3</div>
            <div className="chi-ed-runtime">
              <span>Python 3 v3.10.6</span>
              <RotateCw size={15} />
            </div>
          </div>

          <div className="chi-editor">
            <CodeMirror
              value={code}
              theme="dark"
              height="100%"
              extensions={[python(), indentUnit.of('    ')]}
              onChange={setCode}
              basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true }}
            />
          </div>

          <div className="chi-console">
            <div className="chi-console__tabs">
              <div
                className={`chi-console__tab${consoleTab === 'output' ? ' is-active' : ''}`}
                onClick={() => setConsoleTab('output')}
              >OUTPUT</div>
              <div
                className={`chi-console__tab${consoleTab === 'terminal' ? ' is-active' : ''}`}
                onClick={() => setConsoleTab('terminal')}
              >TERMINAL</div>
              <div className="chi-console__spacer" />
              <div className="chi-console__hint">
                <HelpCircle size={14} />
                <span>Free-format output</span>
              </div>
              <button className="chi-run" onClick={runCode} disabled={isRunning}>
                {isRunning
                  ? <Loader2 size={13} className="chi-spin" />
                  : <Play size={13} fill="#fff" />}
                <span>{isRunning ? 'RUNNING' : 'RUN'}</span>
              </button>
            </div>
            <div className="chi-console__body">
              {consoleTab === 'terminal'
                ? <><b>/usercode/FILESYSTEM</b>$</>
                : <pre className="chi-output">{output || 'Press RUN to execute your code.'}</pre>}
            </div>
          </div>
        </section>
      </div>

      {/* ── Bottom bar ──────────────────────────────────────────── */}
      <footer className="chi-bottombar">
        <div className="chi-more">
          <MoreVertical size={15} />
          <span>MORE</span>
        </div>
        <div className="chi-bottombar__right">
          <button className="chi-btn chi-btn--ghost">VIEW DIFF</button>
          <button className="chi-btn chi-btn--submit">SUBMIT</button>
          <button className="chi-btn chi-btn--ghost">NEXT</button>
        </div>
      </footer>
    </div>
  )
}
